import { sendInternalUiAction } from './internalSink';
import { ANALYTICS_EVENTS } from './eventNames';
import { getRoutePatternForPath } from './routePattern';

/**
 * The root plus the `PublicRoute` pages (`App.tsx`), which redirect a signed-in caller away.
 * A session that exists on one of them is a sign-in in progress (or one the
 * allowlist gate is about to refuse), not a page anybody viewed signed in, so
 * counting it would add a phantom `/login` view to every sign-in.
 */
const SIGNED_OUT_ONLY_PATTERNS: ReadonlySet<string> = new Set([
  // Not a PublicRoute, but the same shape: `Index` renders nothing and sends
  // every signed-in caller on to /dashboard or /welcome. (It holds on `/` while
  // the profile is unread, loading or failed, and such a session is not counted
  // anyway: see AnalyticsRouteTracker's allowlist gate.)
  '/',
  '/login',
  '/create-account',
  '/forgot-password',
  '/request-account',
  '/terms-of-use',
]);

/**
 * KB-4771 — one internal-sink page view for `pathname`, carrying its route
 * pattern as `page` and no params: no ids, no query string, no free text.
 *
 * Kept out of `emitEvent.ts` on purpose. That module is the one path to GA4
 * (`emitEvent.test.ts` pins it), and this event must not reach GA4: GA4 already
 * has its own page_view from `trackPageView` (KB-2607), so sending this there
 * too would double-count every view.
 */
export function emitInternalPageView(pathname: string): void {
  try {
    const page = getRoutePatternForPath(pathname);
    if (SIGNED_OUT_ONLY_PATTERNS.has(page)) return;
    sendInternalUiAction(ANALYTICS_EVENTS.ROUTE_PAGE_VIEW, undefined, { page });
  } catch (err) {
    console.warn('[analytics] internal page view failed to start', err);
  }
}
